/**
 * Workflow Analytics — client → POST /api/analytics/workflow
 *
 * Metadata-only. Every emit() is fire-and-forget: analytics failures never
 * block a chairside action. Timestamps are server-side to prevent client
 * clock drift; the client only supplies a duration_ms delta when relevant.
 *
 * Backend contract (see /app/backend/workflow_analytics.py) is strict —
 * anything not in the ALLOWED_EVENTS allow-list is rejected.
 */

import { api } from "./api";
import { getOrCreateAnalyticsSession } from "./chairsideStore";

// Kept in lockstep with the backend allow-list.
export const EVENTS = Object.freeze({
  SESSION_START: "session.start",
  SESSION_END: "session.end",
  FEATURE_OPEN: "feature.open",
  ASK_LAUNCHED: "ask_the_bench.launched",
  ASK_ANSWERED: "ask_the_bench.answered",
  CASE_INTAKE_STARTED: "case_intake.started",
  CASE_INTAKE_COMPLETED: "case_intake.completed",
  RESUME_LAST_CASE: "resume_last_case.tapped",
  WORKFLOW_DURATION: "workflow.duration",
  NAVIGATION_ROUTE: "navigation.route",
  RESPONSE_TIME: "response.time",
  ERROR_TIMEOUT: "error.timeout",
  ERROR_CLIENT: "error.client",
  CLINICIAN_OVERRIDE: "clinician_override",
  VOICE_COMMAND: "voice.command",
  INPUT_MODE: "input.mode",
  FAVORITE_PINNED: "favorite.pinned",
  FAVORITE_UNPINNED: "favorite.unpinned",
});

function _deviceClass() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth || 0;
  if (w <= 767) return "mobile";
  if (w <= 1024) return "tablet";
  return "desktop";
}

/**
 * Emit a workflow-analytics event. Non-blocking. Never throws.
 *
 * @param {string} event_name  slug from EVENTS
 * @param {object} [extras]    { feature?, duration_ms?, input_mode?, path?, error_code? }
 */
export function emit(event_name, extras = {}) {
  try {
    const session = getOrCreateAnalyticsSession();
    // Trim path so we never leak query strings or dynamic IDs.
    let path = extras.path;
    if (path && typeof path === "string") {
      path = path.split("?")[0].replace(/\/[0-9a-fA-F]{8,}/g, "/:id").slice(0, 120);
    }
    const payload = {
      event_name,
      session_id: session.id,
      device_class: _deviceClass(),
    };
    if (extras.feature) payload.feature = String(extras.feature).slice(0, 60);
    if (Number.isFinite(extras.duration_ms)) {
      payload.duration_ms = Math.max(0, Math.round(extras.duration_ms));
    }
    if (extras.input_mode) payload.input_mode = String(extras.input_mode);
    if (path) payload.path = path;
    if (extras.error_code) payload.error_code = String(extras.error_code).slice(0, 60);
    // Fire-and-forget. Do NOT await, do NOT surface errors.
    api.post("/analytics/workflow", payload).catch(() => { /* swallow */ });
  } catch (_) { /* never throw from analytics */ }
}
